import { createApp } from 'vue';
import App from './App.vue';
import { router } from './router';
import { createMyVuetify, resolveVuetifyLocale } from './plugins/vuetify';
import { iframeDataBridge } from './iframe/iframeDataBridge';
import { i18n } from './i18n';
import { initI18n } from './i18n/bootstrap';
import { ssoPostHandler } from './utils/ssoPostHandler';
import { AllEnterpriseModule, IntegratedChartsModule, ModuleRegistry } from 'ag-grid-enterprise';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { initializeSync } from './sync/dataSync';
import { registerServiceWorker } from './utils/registerServiceWorker';
import { initializeAgGridEnterprise } from './utils/agGridEnterprise';
import { preloadMaterialIconsFont } from './utils/materialIconsPreloader';
import { initSentry, setSentryUser } from './utils/sentry';
import { initMemoryPressure } from './utils/memoryPressure';
import { clearAllMemoryCaches } from './utils/cacheManager';
import { releaseInMemoryFileUrls } from './api/core/file';
import authService from './utils/authService';

import './styles/root.scss';
import './styles/main.scss';

// Must run before mount so SSO cookies are set before the iframe starts loading
ssoPostHandler.init();

const app = createApp(App);

if (import.meta.env.DEV) {
	app.config.devtools = true;
}

ModuleRegistry.registerModules([AllEnterpriseModule, IntegratedChartsModule.with(AgChartsEnterpriseModule)]);

const userLocale = resolveVuetifyLocale(iframeDataBridge.getCurrentUser()?.locale);

app.use(router);
app.use(i18n);
app.use(createMyVuetify(userLocale));

initSentry(app, router);

app.mount('#app');

authService.getContext().then((ctx) => {
	if (ctx?.user) setSentryUser(ctx.user, ctx.user?.customer);
});
initializeAgGridEnterprise();
void preloadMaterialIconsFont();

import { initIframeViewObserver } from './iframe/iframeWatcher';
initIframeViewObserver();

// Relay BO ↔ legacy-iframe postMessages across the extra Vue layer (appointment
// visit/assort dispatch + the webAppReady handshake that flips ecos.webapp.status).
import { initBoMessageRelay } from './iframe/boMessageRelay';
initBoMessageRelay();

// Load the single active language for Vue (reactive) and push it into the legacy
// iframe once it is ready. Fire-and-forget: components update when messages arrive.
void initI18n();

iframeDataBridge.initSSOListener();

initializeSync().catch(console.error);

// Shed RAM before iOS jetsam kills the WebContent process. The native WKWebView shell
// (separate repo) calls window.__ecosMemoryWarning() / posts { type: 'MEMORY_WARNING' };
// see src/utils/memoryPressure.js for the native contract.
initMemoryPressure([releaseInMemoryFileUrls, clearAllMemoryCaches]);

registerServiceWorker();
